import React, { memo, type ReactElement, type ReactNode } from 'react';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { SECTION_ITEM_ROADMAPS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ROADMAPS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { L1_MENU_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { ExpandableMenuItemContent, ExpandableMenuItemTrigger } from '@atlassian/navigation-system';
import { ROADMAP_INTRODUCTION_SPOTLIGHT } from '@atlassian/jira-polaris-lib-onboarding-flows/src/controllers/constants.tsx';
import { RoadmapIntroductionSpotlight } from '@atlassian/jira-polaris-lib-onboarding-flows/src/ui/collections/tour/roadmap-introduction-spotlight/index.tsx';
import { getCollectionsPageUrl } from '@atlassian/jira-polaris-component-collections/src/common/utils/collections.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsJpdProjectView } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-roadmaps/src/common/utils/router.tsx';
import { Footer } from '../../../common/ui/footer/index.tsx';

export type RoadmapsStaticProps = {
	actionsOnHover: ReactElement;
	children?: ReactNode;
	isExpanded: boolean;
	onExpansionToggle?: (isExpanded: boolean) => void;
};

export const RoadmapsStatic = memo((props: RoadmapsStaticProps) => {
	const { isExpanded, onExpansionToggle, actionsOnHover, children } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const isJpdProjectView = useIsJpdProjectView();

	return (
		<SpotlightTarget name={ROADMAP_INTRODUCTION_SPOTLIGHT}>
			<Nav4ExpandableMenuItem
				isExpanded={isExpanded}
				menuId={MENU_ID_ROADMAPS}
				onExpansionToggle={(hasExpanded) => {
					onExpansionToggle?.(hasExpanded);

					if (hasExpanded) {
						sendSectionItemAnalyticsEvent({
							menuId: L1_MENU_ID.ROADMAPS,
							sectionItem: SECTION_ITEM_ROADMAPS,
						});
					}
				}}
			>
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-roadmaps-default"
					actionsOnHover={actionsOnHover}
					aria-label={formattedMessage}
					elemBefore={icon}
					testId={getTestId(MENU_ID_ROADMAPS)}
				>
					{formattedMessage}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{children}
					<Footer />
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{isJpdProjectView && fg('polaris_roadmaps_introduction_spotlight') && (
				<RoadmapIntroductionSpotlight collectionsUrl={getCollectionsPageUrl()} />
			)}
		</SpotlightTarget>
	);
});
